import React from 'react';
import { Document, Page, View, Text, StyleSheet } from '@react-pdf/renderer';

const fmtDate = (d) => {
  if (!d) return '—';
  const s = (typeof d === 'string' ? d : '').slice(0, 10);
  const [y, m, day] = s.split('-');
  return day && m && y ? day + '/' + m + '/' + y : '—';
};
const fmtEur = v => Number(v || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' \u20AC';

const FONTE_LABEL = {
  ordine_fornitore: 'Ordine fornitore',
  report_giornaliero: 'Report giornaliero',
};

const s = StyleSheet.create({
  page: { paddingTop: 71, paddingBottom: 71, paddingLeft: 40, paddingRight: 40, fontSize: 8, fontFamily: 'Helvetica', color: '#1a1a2e' },
  headerBar: { position: 'absolute', top: 0, left: 0, right: 0, height: 5, backgroundColor: '#2EA084' },
  headerWrap: { position: 'absolute', top: 12, left: 40, right: 40, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-end', paddingBottom: 8, borderBottom: '1pt solid #e9ecef' },
  headerLeft: { fontSize: 10, fontFamily: 'Helvetica-Bold', color: '#2EA084' },
  headerRight: { fontSize: 8, color: '#868e96', textAlign: 'right' },
  footer: { position: 'absolute', bottom: 24, left: 40, right: 40, flexDirection: 'row', justifyContent: 'space-between', fontSize: 7, color: '#868e96', borderTop: '0.5pt solid #e9ecef', paddingTop: 6 },
  sectionHead: { borderLeft: '3pt solid #2EA084', paddingLeft: 8, paddingTop: 4, paddingBottom: 4, fontSize: 9, fontFamily: 'Helvetica-Bold', color: '#0A1628', backgroundColor: '#fff', marginBottom: 6, marginTop: 14 },
  kpiRow: { flexDirection: 'row', marginBottom: 8 },
  kpiBox: { border: '1pt solid #e9ecef', borderRadius: 4, padding: '8 10', flex: 1, margin: '0 4' },
  kpiLabel: { fontSize: 7, color: '#868e96', textTransform: 'uppercase', marginBottom: 2 },
  kpiValue: { fontSize: 12, fontFamily: 'Helvetica-Bold' },
  tableHead: { flexDirection: 'row', backgroundColor: '#f8f9fa', borderBottom: '1pt solid #e9ecef', padding: '4 6', fontFamily: 'Helvetica-Bold', fontSize: 7 },
  tableRow: { flexDirection: 'row', borderBottom: '0.5pt solid #e9ecef', padding: '4 6', fontSize: 8 },
  tableRowAlt: { flexDirection: 'row', borderBottom: '0.5pt solid #e9ecef', padding: '4 6', fontSize: 8, backgroundColor: '#f8f9fa' },
  tableRowTotal: { flexDirection: 'row', padding: '5 6', fontSize: 8, fontFamily: 'Helvetica-Bold', backgroundColor: '#F5F5F5', color: '#0A1628', borderTop: '1pt solid #2EA084' },
  groupHead: { borderLeft: '3pt solid #2EA084', paddingLeft: 8, paddingTop: 3, paddingBottom: 3, fontSize: 8, fontFamily: 'Helvetica-Bold', color: '#0A1628', marginTop: 8, marginBottom: 2 },
  bar: { height: 6, borderRadius: 3, marginTop: 4 },
});

export default function SpeseTabPDF({ spese, cantiere, azienda, budget }) {
  const oggi = new Date().toLocaleDateString('it-IT');
  const voci = spese || [];
  const totale = voci.reduce((s, e) => s + Number(e.importo || 0), 0);
  const budgetTotale = budget?.budget_totale || budget?.totale || 0;
  const residuo = budgetTotale - totale;
  const pctSpeso = budgetTotale > 0 ? Math.min(100, (totale / budgetTotale) * 100) : 0;
  const overBudget = pctSpeso > 80;

  // Raggruppa per categoria
  const grouped = {};
  voci.forEach(e => {
    const cat = (e.metadata?.categoria || 'altro').charAt(0).toUpperCase() + (e.metadata?.categoria || 'altro').slice(1);
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(e);
  });

  return (
    <Document>
      <Page size="A4" style={s.page}>
        <View style={s.headerBar} fixed />
        <View style={s.headerWrap} fixed>
          <Text style={s.headerLeft}>Spese cantiere — {cantiere?.titolo || ''}</Text>
          <View style={s.headerRight}>
            <Text>{azienda?.nome || ''}</Text>
            <Text>Generato il {oggi}</Text>
          </View>
        </View>

        <Text style={s.sectionHead}>Riepilogo Budget</Text>
        <View style={s.kpiRow}>
          <View style={s.kpiBox}>
            <Text style={s.kpiLabel}>Budget piano</Text>
            <Text style={s.kpiValue}>{fmtEur(budgetTotale)}</Text>
          </View>
          <View style={s.kpiBox}>
            <Text style={s.kpiLabel}>Totale speso</Text>
            <Text style={[s.kpiValue, { color: '#E85D5D' }]}>{fmtEur(totale)}</Text>
          </View>
          <View style={s.kpiBox}>
            <Text style={s.kpiLabel}>Residuo</Text>
            <Text style={[s.kpiValue, { color: residuo >= 0 ? '#2EA084' : '#E85D5D' }]}>{fmtEur(residuo)}</Text>
          </View>
        </View>
        {budgetTotale > 0 && (
          <View style={{ marginBottom: 8 }}>
            <View style={[s.bar, { backgroundColor: '#e9ecef' }]}>
              <View style={[s.bar, { backgroundColor: overBudget ? '#E85D5D' : '#2EA084', width: pctSpeso + '%', position: 'absolute', top: 0, left: 0 }]} />
            </View>
            {overBudget && <Text style={{ fontSize: 7, color: '#E85D5D', marginTop: 2 }}>Attenzione: speso oltre l'80% del budget</Text>}
          </View>
        )}

        <Text style={s.sectionHead}>Spese per categoria</Text>
        {Object.entries(grouped).map(([cat, lista]) => {
          const totCat = lista.reduce((s, e) => s + Number(e.importo || 0), 0);
          return (
            <View key={cat}>
              <Text style={s.groupHead}>{cat} ({lista.length} voci — {fmtEur(totCat)})</Text>
              <View style={s.tableHead}>
                <Text style={{ flex: 1.5 }}>Data</Text>
                <Text style={{ flex: 3 }}>Descrizione</Text>
                <Text style={{ flex: 1.5 }}>Categoria</Text>
                <Text style={{ flex: 1.5, textAlign: 'right' }}>Importo</Text>
                <Text style={{ flex: 2 }}>Fonte</Text>
              </View>
              {lista.map((e, i) => (
                <View key={e.id} style={i % 2 === 0 ? s.tableRow : s.tableRowAlt}>
                  <Text style={{ flex: 1.5 }}>{fmtDate(e.data_riferimento || e.created_at)}</Text>
                  <Text style={{ flex: 3 }}>{e.contenuto || '—'}</Text>
                  <Text style={{ flex: 1.5 }}>{e.metadata?.categoria || '—'}</Text>
                  <Text style={{ flex: 1.5, textAlign: 'right' }}>{fmtEur(e.importo)}</Text>
                  <Text style={{ flex: 2 }}>{FONTE_LABEL[e.metadata?.fonte] || 'Inserimento manuale'}</Text>
                </View>
              ))}
            </View>
          );
        })}

        <Text style={s.sectionHead}>Totali</Text>
        <View style={s.tableHead}>
          <Text style={{ flex: 3 }}>Categoria</Text>
          <Text style={{ flex: 1, textAlign: 'right' }}>N. voci</Text>
          <Text style={{ flex: 2, textAlign: 'right' }}>Totale</Text>
        </View>
        {Object.entries(grouped).map(([cat, lista], i) => (
          <View key={cat} style={i % 2 === 0 ? s.tableRow : s.tableRowAlt}>
            <Text style={{ flex: 3 }}>{cat}</Text>
            <Text style={{ flex: 1, textAlign: 'right' }}>{lista.length}</Text>
            <Text style={{ flex: 2, textAlign: 'right' }}>{fmtEur(lista.reduce((s, e) => s + Number(e.importo || 0), 0))}</Text>
          </View>
        ))}
        <View style={s.tableRowTotal}>
          <Text style={{ flex: 3 }}>Totale generale</Text>
          <Text style={{ flex: 1, textAlign: 'right' }}>{voci.length}</Text>
          <Text style={{ flex: 2, textAlign: 'right' }}>{fmtEur(totale)}</Text>
        </View>

        <View style={s.footer} fixed>
          <Text>mAIstro — Spese cantiere</Text>
          <Text>{cantiere?.titolo || ''}</Text>
          <Text render={({ pageNumber, totalPages }) => 'Pagina ' + pageNumber + ' di ' + totalPages} />
        </View>
      </Page>
    </Document>
  );
}
